import { Component, Output, EventEmitter, Input } from '@angular/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { ReactiveFormsModule, FormsModule, FormControl } from '@angular/forms';
import { CommonModule } from '@angular/common';
import {
  InUseSerialListDTO,
  SerialListModel,
} from '../models/serial-tab.models';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { map, Observable, startWith } from 'rxjs';
import { QBuiltInspectorService } from '../../services/qbuilit-inspector.service';
import { QBuiltInspectorConstants } from '../../models/constants/qbuilt-inspector.constants';

@Component({
  selector: 'serial-search',
  imports: [
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    ReactiveFormsModule,
    FormsModule,
    CommonModule,
    MatAutocompleteModule,
  ],
  template: `
    <div class="serial-search-container">
      <form class="serial-form">
        <mat-form-field class="serial-search" appearance="outline">
          <mat-label>Serial Number</mat-label>
          <input
            #input
            type="text"
            placeholder="Type Serial Number"
            matInput
            [formControl]="serialNumber"
            [matAutocomplete]="auto"
          />
          <mat-autocomplete
            #auto="matAutocomplete"
            [displayWith]="displayFn"
            (optionSelected)="onSerialSelected($event)"
          >
            @for (option of filteredList | async; track option.serialNumber) {
              <mat-option
                [value]="option"
                [disabled]="option.completed || option.inUse"
                class="custom-option"
              >
                <div>{{ option.serialNumber }}</div>
                @if (option.inUse) {
                  <div class="in-use-current-text">Open in Current Zone</div>
                }
                @if (option.completed) {
                  <div class="completed-text">Complete</div>
                }
              </mat-option>
            }
          </mat-autocomplete>
        </mat-form-field>
      </form>
    </div>
  `,
  styles: `
    .serial-form {
      width: 350px;
      height: 80px;
    }
    .serial-search {
      width: 350px;
      height: 100%;
    }
    .search-btn {
      background-color: transparent;
      color: var(--qbuilt-dark-blue);
      width: 45px;
      text-align: right;
    }
    .search-btn:disabled {
      color: var(--qbuilt-disabled-grey);
    }

    .mat-option.mat-selected {
      background-color: #e0e0e0; /* Example: Light gray background */
      color: #000000; /* Example: Black text color */
      font-weight: bold; /* Example: Bold text */
    }

    .in-use-text {
      color: var(--icon-warning);
      font-weight: bold;
      margin-top: 4px;
      font-size: 0.9em;
    }

    .in-use-current-text {
      color: grey;
      font-weight: bold;
      margin-top: 4px;
      font-size: 0.9em;
    }

    ::ng-deep .completed-text {
      color: var(--qbuilt-light-blue);
      font-weight: bold;
      margin-top: 4px;
      font-size: 0.9em;
    }

    ::ng-deep .mat-mdc-option.mdc-list-item--disabled {
      opacity: 1 !important;
      color: var(--disabled-text) !important;
    }

    ::ng-deep
      .mat-mdc-option.mdc-list-item--disabled
      .mdc-list-item__primary-text {
      opacity: 1 !important;
    }

    ::ng-deep .mat-mdc-option .mdc-list-item__primary-text {
      display: flex !important;
      justify-content: space-between !important;
      align-items: center !important;
      width: 100% !important;
    }
  `,
})
export class SerialSearchComponent {
  //session storage keys
  cqmsSessionKey: string = this.qbuiltInspConstants.cqmsItemsKey;

  @Input() serialNumberList: SerialListModel[] = [];
  @Output() serialCheck = new EventEmitter<string>();

  serialNumber: FormControl = new FormControl('');
  filteredList: Observable<SerialListModel[]>;
  inUseSerialList: SerialListModel[] = [];

  constructor(
    private qbuiltInspService: QBuiltInspectorService,
    private qbuiltInspConstants: QBuiltInspectorConstants,
  ) {}

  ngOnInit() {
    this.serialNumberList =
      JSON.parse(sessionStorage.getItem(this.cqmsSessionKey) || '{}')
        .serialSectionList || [];
    this.serialNumberList.forEach((s) => {
      s.inUse = false;
      s.completed = false;
    });
    const cqmsSessionItems = sessionStorage.getItem(this.cqmsSessionKey)
      ? JSON.parse(sessionStorage.getItem(this.cqmsSessionKey) || '{}')
      : {};
    cqmsSessionItems.selectedSerial = '';
    sessionStorage.setItem(
      this.cqmsSessionKey,
      JSON.stringify(cqmsSessionItems),
    );

    this.filteredList = this.serialNumber.valueChanges.pipe(
      startWith(''),
      map((value) =>
        value ? this._filter(value) : this.serialNumberList.slice(),
      ),
    );

    const zoneId = sessionStorage.getItem(this.cqmsSessionKey)
      ? JSON.parse(sessionStorage.getItem(this.cqmsSessionKey) || '{}').zoneId
      : '';
    this.qbuiltInspService.GetAllInUseSerials(
      localStorage.getItem('facilityNumber'),
      zoneId,
    );
    this.qbuiltInspService.inUseSerials$.subscribe((data) => {
      if (data) {
        this.inUseSerialList = data.map((item: InUseSerialListDTO) => ({
          serialNumber: item.SerialNumber,
          sectionCount: item.SectionCount,
          inUse: item.InUse,
          completed: item.Completed,
        }));

        this.serialNumberList.forEach((serial) => {
          const match = this.inUseSerialList.find(
            (s) => s.serialNumber === serial.serialNumber,
          );
          if (match) {
            serial.completed = match.completed;
            serial.inUse = match.inUse;
          }
        });

        const existingCqmsSessionItems = JSON.parse(
          sessionStorage.getItem(this.cqmsSessionKey) || '{}',
        );
        let cqmsSessionItems: any = {
          ...existingCqmsSessionItems,
          serialSectionList: this.serialNumberList,
        };
        sessionStorage.setItem(
          this.cqmsSessionKey,
          JSON.stringify(cqmsSessionItems),
        );

        const enum SerialRank {
          OpenInZone = 0,
          Available = 1,
          Complete = 2,
        }
        const rank = (s: SerialListModel) => {
          if (s.inUse) return SerialRank.OpenInZone;
          if (s.completed) return SerialRank.Complete;
          return SerialRank.Available;
        };
        this.serialNumberList.sort((a, b) => rank(a) - rank(b));
        this.serialNumber.setValue(this.serialNumber.value, {
          emitEvent: true,
        });
      }
    });
  }

  onSerialSelected(event: any) {
    localStorage.setItem('activeSerial', event.option.value.serialNumber);
    this.serialCheck.emit(event.option.value);
  }

  displayFn(option: SerialListModel): string {
    return option && option.serialNumber ? option.serialNumber : '';
  }

  private _filter(value: SerialListModel | string): SerialListModel[] {
    const filterValue =
      typeof value === 'string'
        ? value.toLowerCase()
        : value.serialNumber.toLowerCase();
    return this.serialNumberList.filter((option) =>
      option.serialNumber.toLowerCase().includes(filterValue),
    );
  }
}
